import { useEffect, useState, useCallback } from "react";
import { Helmet } from "react-helmet-async";
import { useSearchParams, Link } from "react-router-dom";
import {
  Crown, CreditCard, ArrowRight,
  Receipt, WarningCircle, Sparkle, Gear,
} from "@phosphor-icons/react";
import { useAuth } from "../../lib/AuthContext";
import { Button } from "../../components/ui/button";
import { toast } from "sonner";

// Same-origin URL — production (npilookup.com) proxies /api/* to the backend.
const API_BASE = typeof window !== "undefined" ? window.location.origin : (process.env.REACT_APP_BACKEND_URL || "");

const PLAN_LABELS = {
  free: { name: "Free", blurb: "Try every workflow on a small provider list.", tone: "neutral" },
  pro: { name: "Pro", blurb: "Solo billers & small healthcare teams.", tone: "teal" },
  team: { name: "Team", blurb: "Billing companies & credentialing teams.", tone: "navy" },
  business: { name: "Business", blurb: "Staffing firms, MSOs & larger provider operations.", tone: "amber" },
  enterprise: { name: "Enterprise", blurb: "Custom deployments, SSO, and SLA support.", tone: "dark" },
};

const TONE_CLASS = {
  neutral: "border-slate-200 text-slate-900",
  teal: "border-teal/40 text-teal",
  navy: "border-navy/40 text-navy",
  amber: "border-amber-300 text-amber-700",
  dark: "border-slate-800 text-slate-900",
};

const STATUS_COPY = {
  active: { label: "Active", tone: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  trialing: { label: "Trialing", tone: "bg-teal/10 text-teal border-teal/30" },
  past_due: { label: "Past Due", tone: "bg-amber-50 text-amber-700 border-amber-200" },
  canceled: { label: "Canceled", tone: "bg-slate-100 text-slate-600 border-slate-200" },
  incomplete: { label: "Incomplete", tone: "bg-slate-100 text-slate-600 border-slate-200" },
  unpaid: { label: "Unpaid", tone: "bg-red-50 text-red-700 border-red-200" },
};

export default function DashboardSubscription() {
  const { user, authFetch, refreshProfile } = useAuth();
  const [sub, setSub] = useState(null);
  const [loading, setLoading] = useState(true);
  const [portalLoading, setPortalLoading] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const sessionId = searchParams.get("session_id");
  const checkoutStatus = searchParams.get("checkout"); // success | canceled

  const loadSub = useCallback(async () => {
    try {
      const res = await authFetch(`${API_BASE}/api/billing/subscription`);
      if (!res.ok) throw new Error("failed");
      const data = await res.json();
      setSub(data);
    } catch (e) {
      // Non-blocking: probably not configured yet
      setSub(null);
    } finally {
      setLoading(false);
    }
  }, [authFetch]);

  useEffect(() => { loadSub(); }, [loadSub]);

  // After returning from Stripe Checkout success, poll the session status
  // until the webhook (or inline fallback) has applied the subscription.
  useEffect(() => {
    if (checkoutStatus !== "success" || !sessionId) return;
    let cancelled = false;
    let attempts = 0;
    const poll = async () => {
      if (cancelled || attempts > 12) return;
      attempts += 1;
      try {
        const res = await authFetch(`${API_BASE}/api/billing/session/${sessionId}`);
        if (res.ok) {
          const data = await res.json();
          if (data.payment_status === "paid") {
            await Promise.all([loadSub(), refreshProfile?.()]);
            toast.success("Subscription activated!");
            // Clear the URL params
            setSearchParams({}, { replace: true });
            return;
          }
        }
      } catch (_) {}
      setTimeout(poll, 2000);
    };
    poll();
    return () => { cancelled = true; };
  }, [checkoutStatus, sessionId, authFetch, loadSub, refreshProfile, setSearchParams]);

  const tier = sub?.tier || user?.tier || "free";
  const plan = PLAN_LABELS[tier] || PLAN_LABELS.free;
  const status = sub?.subscription_status;
  const statusInfo = status ? STATUS_COPY[status] : null;
  const isAdminEnterprise = sub?.is_admin_enterprise;
  const hasSubscription = sub?.has_subscription;
  const hasStripeCustomer = sub?.has_stripe_customer;

  const periodEnd = sub?.current_period_end ? new Date(sub.current_period_end) : null;

  const openPortal = async () => {
    setPortalLoading(true);
    try {
      const res = await authFetch(`${API_BASE}/api/billing/portal`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ origin_url: window.location.origin }),
      });
      const data = await res.json();
      if (!res.ok) {
        toast.error(data.detail || "Could not open billing portal.");
        setPortalLoading(false);
        return;
      }
      window.location.href = data.url;
    } catch (e) {
      toast.error("Billing portal error.");
      setPortalLoading(false);
    }
  };

  return (
    <>
      <Helmet><title>Subscription | NPI Lookup</title></Helmet>
      <div data-testid="dashboard-subscription">
        <h1 className="font-display font-bold text-2xl text-slate-900 mb-6">Subscription</h1>

        {checkoutStatus === "canceled" && (
          <div className="mb-6 bg-amber-50 border border-amber-200 rounded-lg p-4 flex items-start gap-3" data-testid="checkout-canceled-banner">
            <WarningCircle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" weight="fill" />
            <div className="flex-1 text-sm">
              <div className="font-semibold text-amber-800">Checkout canceled</div>
              <div className="text-amber-700">No changes were made. You can try again from the pricing page.</div>
            </div>
            <Link to="/pricing" className="text-sm font-medium text-amber-800 hover:underline shrink-0">Back to pricing →</Link>
          </div>
        )}

        {checkoutStatus === "success" && (
          <div className="mb-6 bg-emerald-50 border border-emerald-200 rounded-lg p-4 flex items-start gap-3" data-testid="checkout-success-banner">
            <Sparkle className="w-5 h-5 text-emerald-600 shrink-0 mt-0.5" weight="fill" />
            <div className="flex-1 text-sm">
              <div className="font-semibold text-emerald-800">Payment received — activating your plan…</div>
              <div className="text-emerald-700">This page will refresh in a few seconds.</div>
            </div>
          </div>
        )}

        {/* Current plan card */}
        <div className={`bg-white rounded-xl border-2 ${TONE_CLASS[plan.tone] || TONE_CLASS.neutral} p-6 mb-6`}>
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div>
              <div className="flex items-center gap-2 mb-2 flex-wrap">
                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-bold uppercase tracking-wider bg-slate-100 text-slate-700">
                  {tier !== "free" && <Crown className="w-3 h-3" weight="fill" />} Current Plan
                </span>
                {statusInfo && (
                  <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-[11px] font-bold uppercase tracking-wider border ${statusInfo.tone}`} data-testid="subscription-status">
                    {statusInfo.label}
                  </span>
                )}
                {isAdminEnterprise && (
                  <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-bold uppercase tracking-wider bg-navy/10 text-navy border border-navy/20">
                    Admin (Enterprise)
                  </span>
                )}
                {sub?.cancel_at_period_end && (
                  <span className="inline-flex items-center px-2.5 py-1 rounded-full text-[11px] font-bold uppercase tracking-wider bg-amber-50 text-amber-700 border border-amber-200">
                    Cancels at period end
                  </span>
                )}
              </div>
              <h2 className="font-display font-bold text-2xl text-slate-900">{plan.name}</h2>
              <p className="text-sm text-slate-500 mt-1 max-w-md">{plan.blurb}</p>
            </div>

            <div className="flex flex-col items-end gap-2">
              {hasStripeCustomer ? (
                <Button
                  onClick={openPortal}
                  disabled={portalLoading}
                  className="bg-slate-900 hover:bg-slate-800 text-white rounded-lg gap-2"
                  data-testid="manage-billing-btn"
                >
                  <CreditCard className="w-4 h-4" />
                  {portalLoading ? "Opening…" : "Manage billing"}
                </Button>
              ) : (
                tier === "free" && (
                  <Link to="/pricing">
                    <Button className="bg-teal hover:bg-teal/90 text-white rounded-lg gap-2" data-testid="see-pricing-btn">
                      See plans <ArrowRight className="w-4 h-4" />
                    </Button>
                  </Link>
                )
              )}
              {periodEnd && (
                <p className="text-xs text-slate-500" data-testid="period-end">
                  {sub?.cancel_at_period_end ? "Cancels" : "Renews"}{" "}
                  {periodEnd.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })}
                </p>
              )}
            </div>
          </div>

          {/* Plan summary */}
          {hasSubscription && (
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-6 pt-5 border-t border-slate-100">
              <div>
                <div className="text-xs uppercase tracking-wider text-slate-500 mb-1">Plan</div>
                <div className="text-sm font-semibold text-slate-900">{plan.name}</div>
              </div>
              <div>
                <div className="text-xs uppercase tracking-wider text-slate-500 mb-1">Billing</div>
                <div className="text-sm font-semibold text-slate-900 capitalize">{sub?.plan_interval || "—"}</div>
              </div>
              <div>
                <div className="text-xs uppercase tracking-wider text-slate-500 mb-1">Extra seats</div>
                <div className="text-sm font-semibold text-slate-900">{sub?.seat_count || 0}</div>
              </div>
              <div>
                <div className="text-xs uppercase tracking-wider text-slate-500 mb-1">Status</div>
                <div className="text-sm font-semibold text-slate-900 capitalize">{statusInfo?.label || "—"}</div>
              </div>
            </div>
          )}
        </div>

        {/* Usage */}
        <div className="bg-white rounded-xl border border-slate-200 p-6 mb-6">
          <h3 className="font-display font-semibold text-base text-slate-900 mb-3">Usage</h3>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 text-sm">
            <div className="bg-slate-50 rounded-lg p-3">
              <p className="text-xl font-bold text-slate-900">{user?.lookup_count_today || 0}</p>
              <p className="text-xs text-slate-500">Lookups today</p>
            </div>
            <div className="bg-slate-50 rounded-lg p-3">
              <p className="text-xl font-bold text-slate-900">{user?.lookup_count_total || 0}</p>
              <p className="text-xs text-slate-500">Lookups all time</p>
            </div>
          </div>
        </div>

        {/* Plan actions */}
        {tier !== "enterprise" && (
          <div className="bg-white rounded-xl border border-slate-200 p-6 mb-6">
            <h3 className="font-display font-semibold text-base text-slate-900 mb-1">
              {tier === "free" ? "Upgrade" : "Change plan"}
            </h3>
            <p className="text-sm text-slate-500 mb-4">
              {tier === "free"
                ? "Unlock higher monitoring list size, bulk screening, and exports."
                : "Switch plans, update seats, pause, or cancel from the Stripe billing portal."}
            </p>
            <div className="flex flex-wrap gap-3">
              <Link to="/pricing">
                <Button variant="outline" className="rounded-lg gap-2" data-testid="view-all-plans-btn">
                  View all plans <ArrowRight className="w-4 h-4" />
                </Button>
              </Link>
              {hasStripeCustomer && (
                <Button
                  onClick={openPortal}
                  disabled={portalLoading}
                  className="rounded-lg gap-2 bg-teal hover:bg-teal/90 text-white"
                  data-testid="manage-plan-portal-btn"
                >
                  <Gear className="w-4 h-4" />
                  {portalLoading ? "Opening…" : "Manage in Stripe"}
                </Button>
              )}
            </div>
          </div>
        )}

        {/* Invoices pointer */}
        <div className="bg-white rounded-xl border border-slate-200 p-6">
          <h3 className="font-display font-semibold text-base text-slate-900 mb-3 flex items-center gap-2">
            <Receipt className="w-5 h-5 text-slate-400" /> Invoices & payment methods
          </h3>
          {hasStripeCustomer ? (
            <p className="text-sm text-slate-600">
              Invoices, receipts, payment methods, and billing address are all managed in the Stripe billing portal.{" "}
              <button onClick={openPortal} className="text-teal hover:underline font-medium">Open portal →</button>
            </p>
          ) : (
            <p className="text-sm text-slate-500">
              Once you subscribe, you'll be able to download invoices, update payment methods, and cancel anytime from here.
            </p>
          )}
        </div>
      </div>
    </>
  );
}
